/**
 * Icon geometry, one entry per icon.
 *
 * The prototype pasted the same SVG markup at each call site — the refresh icon alone
 * appeared four times. Here the shapes live once and `<BaseIcon name="…">` draws them.
 *
 * `stroke: false` marks the handful of solid icons (the transport controls); everything
 * else is the stroked, 24×24 outline set the design uses throughout.
 */
export default {
  clock: {
    shapes: [
      { tag: "circle", attrs: { cx: 12, cy: 12, r: 10 } },
      { tag: "polyline", attrs: { points: "12 7 12 12 15 14" } },
    ],
  },
  phone: {
    shapes: [
      {
        tag: "path",
        attrs: {
          d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z",
        },
      },
    ],
  },
  camera: {
    shapes: [
      {
        tag: "path",
        attrs: { d: "M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" },
      },
      { tag: "circle", attrs: { cx: 12, cy: 13, r: 3.2 } },
    ],
  },
  help: {
    shapes: [
      { tag: "circle", attrs: { cx: 12, cy: 12, r: 10 } },
      { tag: "path", attrs: { d: "M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" } },
      { tag: "path", attrs: { d: "M12 17h.01" } },
    ],
  },
  refresh: {
    shapes: [
      { tag: "path", attrs: { d: "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" } },
      { tag: "path", attrs: { d: "M21 3v5h-5" } },
      { tag: "path", attrs: { d: "M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" } },
      { tag: "path", attrs: { d: "M8 16H3v5" } },
    ],
  },
  check: {
    shapes: [{ tag: "path", attrs: { d: "M20 6 9 17l-5-5" } }],
  },
  "check-circle": {
    shapes: [
      { tag: "path", attrs: { d: "M22 11.08V12a10 10 0 1 1-5.93-9.14" } },
      { tag: "polyline", attrs: { points: "22 4 12 14.01 9 11.01" } },
    ],
  },
  sun: {
    shapes: [
      { tag: "circle", attrs: { cx: 12, cy: 12, r: 4 } },
      {
        tag: "path",
        attrs: { d: "M12 3v1M12 20v1M4.9 4.9l.7.7M18.4 18.4l.7.7M3 12h1M20 12h1M4.9 19.1l.7-.7M18.4 5.6l.7-.7" },
      },
    ],
  },
  lock: {
    shapes: [
      { tag: "rect", attrs: { width: 18, height: 11, x: 3, y: 11, rx: 2 } },
      { tag: "path", attrs: { d: "M7 11V7a5 5 0 0 1 10 0v4" } },
    ],
  },
  alert: {
    shapes: [
      { tag: "path", attrs: { d: "m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3Z" } },
      { tag: "path", attrs: { d: "M12 9v4" } },
      { tag: "path", attrs: { d: "M12 17h.01" } },
    ],
  },
  "wifi-off": {
    shapes: [
      { tag: "path", attrs: { d: "M2 8.8a16 16 0 0 1 20 0" } },
      { tag: "path", attrs: { d: "M5 12.5a11 11 0 0 1 14 0" } },
      { tag: "path", attrs: { d: "M8.5 16a6 6 0 0 1 7 0" } },
      { tag: "line", attrs: { x1: 2, y1: 2, x2: 22, y2: 22 } },
    ],
  },
  "arrow-left": {
    shapes: [
      { tag: "path", attrs: { d: "m12 19-7-7 7-7" } },
      { tag: "path", attrs: { d: "M19 12H5" } },
    ],
  },
  "arrow-right": {
    shapes: [
      { tag: "path", attrs: { d: "M5 12h14" } },
      { tag: "path", attrs: { d: "m12 5 7 7-7 7" } },
    ],
  },
  record: {
    shapes: [
      { tag: "circle", attrs: { cx: 12, cy: 12, r: 9 } },
      { tag: "circle", attrs: { cx: 12, cy: 12, r: 4, fill: "currentColor" } },
    ],
  },
  volume: {
    shapes: [
      { tag: "path", attrs: { d: "M11 4.7 6.5 8.5H3v7h3.5L11 19.3z" } },
      { tag: "path", attrs: { d: "M16 9a5 5 0 0 1 0 6" } },
      { tag: "path", attrs: { d: "M19.4 6.6a9 9 0 0 1 0 10.8" } },
    ],
  },
  edit: {
    shapes: [
      { tag: "path", attrs: { d: "M12 20h9" } },
      { tag: "path", attrs: { d: "M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" } },
    ],
  },
  trash: {
    shapes: [
      { tag: "path", attrs: { d: "M3 6h18" } },
      { tag: "path", attrs: { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" } },
      { tag: "path", attrs: { d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" } },
    ],
  },
  copy: {
    shapes: [
      { tag: "rect", attrs: { width: 13, height: 13, x: 9, y: 9, rx: 2 } },
      { tag: "path", attrs: { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" } },
    ],
  },
  message: {
    shapes: [{ tag: "path", attrs: { d: "M7.9 20A9 9 0 1 0 4 16.1L2 22Z" } }],
  },
  close: {
    shapes: [
      { tag: "path", attrs: { d: "M18 6 6 18" } },
      { tag: "path", attrs: { d: "m6 6 12 12" } },
    ],
  },

  // --- solid ---
  play: {
    stroke: false,
    shapes: [
      {
        tag: "path",
        attrs: { d: "M8 5.5v13a1 1 0 0 0 1.54.84l10-6.5a1 1 0 0 0 0-1.68l-10-6.5A1 1 0 0 0 8 5.5z" },
      },
    ],
  },
  pause: {
    stroke: false,
    shapes: [
      { tag: "rect", attrs: { x: 6, y: 5, width: 4, height: 14, rx: 1.2 } },
      { tag: "rect", attrs: { x: 14, y: 5, width: 4, height: 14, rx: 1.2 } },
    ],
  },
};
